import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const category = article ? categories.find((c) => c.slug === article.category) : null;
  const relatedArticles = article
    ? articles.filter((a) => a.category === article.category && a.slug !== article.slug).slice(0, 4)
    : [];

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-800">文章不存在</h1>
            <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700 text-sm whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />

      <main>
        {/* Article Hero */}
        <section className="relative w-full min-h-[420px] md:min-h-[500px] flex items-end pb-10">
          <div className="absolute inset-0">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-black/45"></div>
          </div>

          <div className="relative z-10 w-full px-4 md:px-6">
            <div className="w-full max-w-[1200px] mx-auto">
              <div className="flex flex-col lg:flex-row items-start lg:items-end gap-8 pt-20 lg:pt-0">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    {article.hot && (
                      <span className="px-2.5 py-0.5 text-[10px] font-semibold rounded-full bg-accent-500 text-background-50 whitespace-nowrap">
                        热门
                      </span>
                    )}
                    {article.exclusive && (
                      <span className="px-2.5 py-0.5 text-[10px] font-semibold rounded-full bg-primary-500 text-background-50 whitespace-nowrap">
                        独家
                      </span>
                    )}
                    {category && (
                      <Link
                        to={`/category/${article.category}`}
                        className="px-2.5 py-0.5 text-[10px] font-semibold rounded-full bg-white/20 text-background-50 whitespace-nowrap hover:bg-white/30 transition-colors"
                      >
                        {category.name}
                      </Link>
                    )}
                  </div>
                  <h1 className="text-xl md:text-3xl lg:text-4xl font-heading font-bold text-background-50 leading-tight tracking-tight">
                    {article.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-3 mt-4 text-xs text-background-200">
                    <span>{article.author}</span>
                    <span className="w-1 h-1 rounded-full bg-background-400"></span>
                    <span>{article.publishDate}</span>
                    <span className="w-1 h-1 rounded-full bg-background-400"></span>
                    <span>{article.readTime}阅读</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-background-300 text-background-50 text-xs font-medium hover:bg-white/10 transition-all duration-200 whitespace-nowrap" aria-label="收藏">
                    <i className="ri-bookmark-line text-sm"></i>
                    收藏
                  </button>
                  <button className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-background-300 text-background-50 text-xs font-medium hover:bg-white/10 transition-all duration-200 whitespace-nowrap" aria-label="分享">
                    <i className="ri-share-forward-line text-sm"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6 py-4">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category?.name || '分类', href: category ? `/category/${category.slug}` : undefined },
              { label: article.title },
            ]}
          />
        </div>

        {/* Article Content */}
        <article className="pb-10 bg-background-50">
          <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10">
              {/* Main Content */}
              <div className="flex-1 max-w-none lg:max-w-[780px]">
                <div
                  className="prose prose-base md:prose-lg max-w-none prose-headings:font-heading prose-headings:text-foreground-900 prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:text-sm md:prose-p:text-base prose-strong:text-foreground-900"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                {/* Tags */}
                <div className="mt-8 pt-6 border-t border-background-200/70">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-foreground-400">标签：</span>
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600 whitespace-nowrap"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <aside className="lg:w-[320px] shrink-0">
                <div className="sticky top-24">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-4">相关爆料</h3>
                  <div className="flex flex-col gap-3">
                    {relatedArticles.map((related) => (
                      <Link
                        key={related.slug}
                        to={`/article/${related.slug}`}
                        className="group flex gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors duration-200"
                      >
                        <div className="w-[80px] h-[56px] shrink-0 rounded-md overflow-hidden">
                          <img
                            src={related.imageUrl}
                            alt={related.title}
                            className="w-full h-full object-cover object-top"
                            loading="lazy"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-xs font-medium text-foreground-700 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200 leading-snug">
                            {related.title}
                          </h4>
                          <span className="text-[10px] text-foreground-400 mt-1 block">{related.publishDate}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </article>

        {/* Editor Quote Section */}
        <section className="py-14 md:py-18 bg-background-100">
          <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-10">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-2 h-2 rounded-full bg-accent-500"></span>
                  <span className="text-[11px] font-medium text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    小编辣评
                  </span>
                </div>
                <blockquote className="text-xl md:text-2xl lg:text-3xl font-heading font-semibold text-foreground-900 italic leading-relaxed">
                  &ldquo;黑料的本质不是恶意中伤，而是还原被刻意隐藏的事实。
                  每一条爆料的背后，都有无数个选择沉默的人。
                  我们选择发声，只因为真相值得被看到。&rdquo;
                </blockquote>
                <div className="mt-4 flex items-center gap-3">
                  <span className="text-sm font-semibold text-foreground-700">五一黑料编辑部</span>
                  <span className="flex items-center gap-1 text-xs text-accent-600">
                    <i className="ri-verified-badge-line"></i>
                    已认证
                  </span>
                </div>
              </div>

              <div className="lg:w-[340px] shrink-0">
                <div className="aspect-[3/4] rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20editorial%20portrait%20photography%2C%20side%20profile%20of%20person%20in%20dark%20suit%2C%20black%20and%20white%20with%20warm%20cream%20tone%2C%20dramatic%20studio%20lighting%2C%20magazine%20editorial%20style%2C%20clean%20minimalist%20background%2C%20confident%20and%20trustworthy%20expression&width=600&height=800&seq=editor-portrait&orientation=portrait"
                    alt="五一黑料网编辑团队"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Author Statement */}
        <section className="py-14 md:py-18 bg-accent-50">
          <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-[200px] shrink-0">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-foreground-500 whitespace-nowrap">
                  <span className="w-3 h-[1px] bg-foreground-400"></span>
                  编辑声明
                </span>
              </div>
              <div className="flex-1">
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  本文内容由五一黑料网编辑团队独立调查和撰写。我们坚持"多方核实、去伪存真"的报道原则，
                  所有爆料内容均经过至少三个独立信源的交叉验证。我们承诺不发布未经证实的传闻，
                  不对当事人进行恶意揣测和人身攻击。如有不实之处，欢迎当事方通过官方渠道与我们沟通更正。
                </p>
                <div className="mt-6 flex items-center gap-4">
                  <span className="text-[11px] text-foreground-400">
                    <span className="w-1.5 h-1.5 rounded-full bg-foreground-300 inline-block mr-1.5"></span>
                    编辑团队
                  </span>
                  <div className="flex -space-x-2">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="w-8 h-8 rounded-full bg-primary-200 border-2 border-accent-50 flex items-center justify-center">
                        <i className="ri-user-line text-xs text-primary-600"></i>
                      </div>
                    ))}
                  </div>
                  <span className="text-[11px] text-foreground-500">五一黑料网编辑委员会 联合出品</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Articles Full Grid */}
        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-18 bg-background-50">
            <div className="w-full max-w-[1400px] mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 tracking-tight">
                相关爆料推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedArticles.map((related) => (
                  <ArticleCard key={related.slug} article={related} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            datePublished: article.publishDate,
            author: {
              '@type': 'Organization',
              name: article.author,
            },
            publisher: {
              '@type': 'Organization',
              name: '五一黑料网',
              url: 'https://www.wuyiheiliao.com/',
            },
            url: `https://www.wuyiheiliao.com/article/${article.slug}`,
            image: article.imageUrl,
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://www.wuyiheiliao.com/article/${article.slug}`,
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://www.wuyiheiliao.com/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: category?.name || '分类',
                  item: category ? `https://www.wuyiheiliao.com/category/${category.slug}` : 'https://www.wuyiheiliao.com/',
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: article.title,
                },
              ],
            },
          }),
        }}
      />
    </>
  );
}